/**
 * Access facts (gate code, dog, where things are) and the customer's
 * reference photos. Read from the server - which access-logs the read,
 * scoped to the driver's own stop on an active route - and cached in
 * IndexedDB at route load so they are there in a driveway with no signal.
 * Photos are stored as Blobs so a presigned URL expiring changes nothing.
 */
import { apiFetch } from './api'
import { getAccess, getAsset, putAccess, putAsset } from './db'

export interface AccessFacts {
  drop_location: '' | 'front_door' | 'back_door' | 'garage' | 'other'
  garage_code: string
  gate_location: string
  gate_code: string
  latch_notes: string
  dog_present: boolean
  dog_loose: boolean
  dog_notes: string
  feed_storage_location: string
  waterer_location: string
  feeder_location: string
  grit_location: string
  shell_location: string
  notes: string
  captured_at: string | null
  surveyed_at: string | null
}

export interface ReferencePhoto {
  id: number
  kind: string
  caption: string
  url: string
  /** customer | walk | driver (delivery/driver_findings.py). */
  source?: string
  added_by?: string
}

/** What drivers FOUND, per location, beside the customer's words - shown first. */
export type FoundNotes = Record<string, { text: string; by: string; at: string }>

export interface StopAccess {
  access: AccessFacts | null
  reference_photos: ReferencePhoto[]
  property_capture_skipped: boolean
  found?: FoundNotes
}

export async function fetchStopAccess(stopUuid: string): Promise<StopAccess> {
  const res = await apiFetch(`/delivery/driver/stops/${stopUuid}/access/`)
  if (!res.ok) throw new Error(`access ${res.status}`)
  return (await res.json()) as StopAccess
}

/** Cache every stop's access facts and photos. Best effort per stop; a stop
 *  that fails simply keeps whatever it had. */
export async function prefetchAccess(stopUuids: string[]): Promise<{ ok: number; failed: number }> {
  let ok = 0
  let failed = 0
  for (const uuid of stopUuids) {
    try {
      const data = await fetchStopAccess(uuid)
      await putAccess(uuid, data)
      for (const photo of data.reference_photos) {
        try {
          const r = await fetch(photo.url)
          if (r.ok) {
            const blob = await r.blob()
            await putAsset(`${uuid}:${photo.id}`, uuid, blob, blob.type || 'image/jpeg')
          }
        } catch {
          /* the facts are cached; the picture can come later */
        }
      }
      ok += 1
    } catch {
      failed += 1
    }
  }
  return { ok, failed }
}

export async function cachedAccess(stopUuid: string): Promise<StopAccess | undefined> {
  const row = await getAccess<StopAccess>(stopUuid)
  return row?.access
}

export async function referencePhotoObjectUrl(stopUuid: string, photo: ReferencePhoto): Promise<string> {
  const blob = await getAsset(`${stopUuid}:${photo.id}`)
  return blob ? URL.createObjectURL(blob) : photo.url
}
